import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { escalationRuleEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { EscalationRule, EscalationRuleInput } from '@/types/escalationRule';

export function useEscalationRules() {
  return useApiQuery<EscalationRule[]>({
    queryKey: settingsKeys.escalationRules(),
    queryFn: () => apiGet<EscalationRule[]>(operationsClient, escalationRuleEndpoints.list()),
    showErrorToast: false,
  });
}

function useInvalidateEscalationRules() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: settingsKeys.escalationRules() });
  };
}

export function useCreateEscalationRule() {
  const invalidate = useInvalidateEscalationRules();
  return useApiMutation<EscalationRule, EscalationRuleInput>({
    mutationFn: (dto) =>
      apiPost<EscalationRule, EscalationRuleInput>(
        operationsClient,
        escalationRuleEndpoints.list(),
        dto,
      ),
    onSuccess: (rule) => {
      invalidate();
      toast.success(`Created ${rule.name}`);
    },
    showErrorToast: true,
  });
}

export function useUpdateEscalationRule() {
  const invalidate = useInvalidateEscalationRules();
  return useApiMutation<EscalationRule, { id: string; dto: Partial<EscalationRuleInput> }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<EscalationRule, Partial<EscalationRuleInput>>(
        operationsClient,
        escalationRuleEndpoints.detail(id),
        dto,
      ),
    onSuccess: (rule) => {
      invalidate();
      toast.success(`Updated ${rule.name}`);
    },
    showErrorToast: true,
  });
}

export function useDeleteEscalationRule() {
  const invalidate = useInvalidateEscalationRules();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, escalationRuleEndpoints.detail(id)),
    onSuccess: () => {
      invalidate();
      toast.success('Price increase schedule deleted');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : '';
      const match = message.match(/referenced by (\d+) PricingTerms/i);
      if (match) {
        toast.error(`In use by ${match[1]} contracts - detach or change those contracts first.`);
        return;
      }
      toast.error(message || 'Could not delete price increase schedule');
    },
    showErrorToast: false,
  });
}
